import classNames from 'classnames'
import { FormikErrors } from 'formik'
import { MouseEvent, useState } from 'react'
import {
  BundlePin,
  CreateProductValues,
} from '../../redux-toolkits/products/products.type'
import { HeaderSubheader } from '../typography/HeaderSubheader'

const round = (value: number) => Math.round(value * 10000) / 10000

/**
 * Tags the bundle's items on its main image (the first one). Clicking the
 * image drops a pin for an item, numbered by its place in the bundle;
 * clicking again for the same item moves its pin.
 */
export const BundleItemPins = ({
  values,
  setFieldValue,
}: {
  values: CreateProductValues
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean,
  ) => Promise<void> | Promise<FormikErrors<CreateProductValues>>
}) => {
  const items = values.bundledProducts ?? []
  const image = values.images[0]
  // The spot clicked on the image, waiting for an item to be picked.
  const [spot, setSpot] = useState<BundlePin>()

  const setPin = (index: number, pin: BundlePin | null) => {
    setFieldValue(
      'bundledProducts',
      items.map((item, i) => (i === index ? { ...item, pin } : item)),
    )
  }

  const pickSpot = (event: MouseEvent<HTMLImageElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    setSpot({
      x: round((event.clientX - rect.left) / rect.width),
      y: round((event.clientY - rect.top) / rect.height),
    })
  }

  if (!items.length) return null

  return (
    <div className="flex flex-col gap-6">
      <HeaderSubheader
        title="Tag items"
        text={
          image
            ? 'Click the bundle image where an item is, then pick which item it is'
            : 'Upload a bundle image above to tag where each item is'
        }
      />

      {image && (
        <>
          <div className="relative w-full max-w-[560px] select-none">
            <img
              src={image}
              alt="Bundle"
              className="w-full rounded-lg cursor-crosshair"
              onClick={pickSpot}
            />

            {items.map((item, index) =>
              item.pin ? (
                <span
                  key={item.product._id}
                  title={item.product.name}
                  className="absolute -translate-x-1/2 -translate-y-1/2 size-8 rounded-full bg-white/80 text-black text-sm font-medium flex items-center justify-center shadow pointer-events-none"
                  style={{
                    left: `${item.pin.x * 100}%`,
                    top: `${item.pin.y * 100}%`,
                  }}
                >
                  {index + 1}
                </span>
              ) : null,
            )}

            {spot && (
              <>
                <span
                  className="absolute -translate-x-1/2 -translate-y-1/2 size-8 rounded-full bg-black border-2 border-white pointer-events-none"
                  style={{ left: `${spot.x * 100}%`, top: `${spot.y * 100}%` }}
                />
                <div
                  className={classNames(
                    'absolute z-10 mt-5 w-[240px] max-h-[260px] overflow-y-auto rounded-lg bg-white shadow-lg border flex flex-col py-2',
                    { '-translate-x-full': spot.x > 0.5 },
                  )}
                  style={{ left: `${spot.x * 100}%`, top: `${spot.y * 100}%` }}
                >
                  <p className="px-4 py-1 text-sm text-sec-black">
                    Which item is this?
                  </p>
                  {items.map((item, index) => (
                    <button
                      key={item.product._id}
                      type="button"
                      className="px-4 py-2 text-left text-sm hover:bg-off-white flex gap-2"
                      onClick={() => {
                        setPin(index, spot)
                        setSpot(undefined)
                      }}
                    >
                      <span className="font-medium">{index + 1}</span>
                      <span className="flex-1">{item.product.name}</span>
                      {item.pin && <span className="text-sec-black">Move</span>}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="px-4 py-2 text-left text-sm text-sec-black hover:bg-off-white"
                    onClick={() => setSpot(undefined)}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="flex flex-col gap-2 max-w-[560px]">
            {items.map((item, index) => (
              <div
                key={item.product._id}
                className="flex items-center gap-3 text-sm"
              >
                <span className="size-6 rounded-full border flex items-center justify-center font-medium">
                  {index + 1}
                </span>
                <span className="flex-1">{item.product.name}</span>
                {item.pin ? (
                  <button
                    type="button"
                    className="text-danger"
                    onClick={() => setPin(index, null)}
                  >
                    Remove pin
                  </button>
                ) : (
                  <span className="text-sec-black">Not tagged</span>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
